/**
 * 🎨 AffiliateSidebar - Dark Theme Navigation
 * 
 * Features:
 * - Dark Slate Background (#0f172a)
 * - Collapsible für Mobile
 * - Active State mit grünem Accent
 * - User Info Section am Bottom
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { 
  LayoutDashboard, 
  Users, 
  Wallet, 
  CreditCard, 
  Image as ImageIcon, 
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Store,
  TrendingUp,
  type LucideIcon
} from 'lucide-react';
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// ============================================
// TYPES
// ============================================

export type AffiliateLevel = 'starter' | 'bronze' | 'silber' | 'gold' | 'platin' | 'diamant';

export interface AffiliateUser {
  name: string;
  email: string;
  avatar?: string;
  level: AffiliateLevel;
}

export interface NavItem {
  id: string;
  label: string;
  icon: LucideIcon;
  href: string;
  badge?: string | number;
}

export interface AffiliateSidebarProps {
  /** Aktueller Navigation State */
  activeRoute: string;
  /** Collapsed State */
  isCollapsed?: boolean;
  /** User Info */
  user: AffiliateUser;
  /** Callback bei Navigation */
  onNavigate: (route: string) => void;
  /** Callback bei Collapse Toggle */
  onCollapse?: () => void;
  /** Callback bei Logout */
  onLogout?: () => void;
  /** Zusätzliche CSS Klassen */
  className?: string;
}

// ============================================
// CONSTANTS
// ============================================

const NAV_ITEMS: NavItem[] = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, href: '/affiliate' },
  { id: 'leads', label: 'Leads', icon: Users, href: '/affiliate/leads' },
  { id: 'lokale', label: 'Meine Lokale', icon: Store, href: '/affiliate/lokale' },
  { id: 'provisionen', label: 'Provisionen', icon: Wallet, href: '/affiliate/provisionen' },
  { id: 'konstantleiter', label: 'Stufenleiter', icon: TrendingUp, href: '/affiliate/konstantleiter' },
  { id: 'marketing', label: 'Werbemittel', icon: ImageIcon, href: '/affiliate/marketing' },
  { id: 'zahlungen', label: 'Zahlungen', icon: CreditCard, href: '/affiliate/zahlungen' },
  { id: 'einstellungen', label: 'Einstellungen', icon: Settings, href: '/affiliate/einstellungen' },
];

const LEVEL_CONFIG: Record<AffiliateLevel, { label: string; icon: string; color: string; bgColor: string }> = {
  starter: {
    label: 'Starter',
    icon: '🌱',
    color: 'text-green-500',
    bgColor: 'bg-green-500/10',
  },
  bronze: { 
    label: 'Bronze',
    icon: '🥉',
    color: 'text-amber-500',
    bgColor: 'bg-amber-500/10',
  },
  silber: {
    label: 'Silber',
    icon: '🥈',
    color: 'text-gray-400',
    bgColor: 'bg-gray-400/10',
  },
  gold: {
    label: 'Gold',
    icon: '🥇',
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-400/10',
  },
  platin: {
    label: 'Platin',
    icon: '💎',
    color: 'text-cyan-300',
    bgColor: 'bg-cyan-300/10',
  },
  diamant: {
    label: 'Diamant',
    icon: '💠',
    color: 'text-purple-400',
    bgColor: 'bg-purple-400/10',
  },
};

// ============================================
// SUB-COMPONENTS
// ============================================

interface NavItemButtonProps {
  item: NavItem;
  isActive: boolean;
  isCollapsed: boolean;
  onClick: () => void;
}

const NavItemButton: React.FC<NavItemButtonProps> = ({ 
  item, 
  isActive, 
  isCollapsed, 
  onClick 
}) => {
  const Icon = item.icon;
  
  const button = (
    <button
      onClick={onClick}
      aria-current={isActive ? 'page' : undefined}
      aria-label={item.label}
      className={cn(
        'w-full flex items-center gap-3 px-4 h-11 rounded-xl',
        'transition-colors duration-200',
        'text-white/60 hover:text-white hover:bg-white/10',
        isActive && [
          'text-white bg-gradient-to-r from-emerald-500/20 to-emerald-500/5',
          'shadow-[inset_0_0_0_1px_rgba(16,185,129,0.3)]',
        ],
        isCollapsed && 'justify-center px-3',
      )}
    >
      <Icon className={cn(
        'w-5 h-5 flex-shrink-0 transition-colors',
        isActive ? 'text-emerald-400' : 'text-white/60'
      )} />
      
      {!isCollapsed && (
        <>
          <span className={cn(
            'flex-1 text-left text-sm font-medium',
            isActive && 'text-white'
          )}>
            {item.label}
          </span>
          
          {item.badge && (
            <span className={cn(
              'px-2 py-0.5 text-xs font-semibold rounded-full',
              'bg-emerald-500/20 text-emerald-400'
            )}>
              {item.badge}
            </span>
          )}
        </>
      )}
    </button>
  );

  if (isCollapsed) {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          {button}
        </TooltipTrigger>
        <TooltipContent side="right" className="font-medium">
          {item.label}
          {item.badge && ` (${item.badge})`}
        </TooltipContent>
      </Tooltip>
    );
  }

  return button;
};

interface UserSectionProps {
  user: AffiliateUser;
  isCollapsed: boolean;
  onLogout?: () => void;
}

const UserSection: React.FC<UserSectionProps> = ({ user, isCollapsed, onLogout }) => {
  const levelConfig = LEVEL_CONFIG[user.level];
  
  if (isCollapsed) {
    return (
      <div className="px-3 py-4 border-t border-white/10">
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <button className="w-full flex justify-center">
              <div className={cn(
                'w-10 h-10 rounded-full flex items-center justify-center',
                'bg-slate-700 text-white font-semibold text-sm'
              )}>
                {user.avatar ? (
                  <img src={user.avatar} alt={user.name} className="w-full h-full rounded-full object-cover" />
                ) : (
                  user.name.charAt(0).toUpperCase()
                )}
              </div>
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">
            <div className="font-medium">{user.name}</div>
            <div className="text-xs text-muted-foreground">{levelConfig.label}</div>
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className="px-4 py-4 border-t border-white/10">
      <div className="flex items-center gap-3 mb-3">
        {/* Avatar */}
        <div className={cn(
          'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0',
          'bg-slate-700 text-white font-semibold text-sm'
        )}>
          {user.avatar ? (
            <img src={user.avatar} alt={user.name} className="w-full h-full rounded-full object-cover" />
          ) : (
            user.name.charAt(0).toUpperCase()
          )}
        </div>
        
        {/* User Info */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-white truncate">
            {user.name}
          </div>
          <div className={cn(
            'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs',
            levelConfig.bgColor, levelConfig.color
          )}>
            <span>{levelConfig.icon}</span>
            <span>{levelConfig.label}</span>
          </div>
        </div>
      </div>
      
      {/* Logout Button */}
      {onLogout && (
        <button
          onClick={onLogout}
          className={cn(
            'w-full flex items-center gap-2 px-3 py-2 rounded-lg',
            'text-sm text-white/60 hover:text-white hover:bg-slate-800',
            'transition-colors duration-200'
          )}
        >
          <LogOut className="w-4 h-4" />
          <span>Abmelden</span>
        </button>
      )}
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

export const AffiliateSidebar: React.FC<AffiliateSidebarProps> = ({
  activeRoute,
  isCollapsed = false,
  user,
  onNavigate,
  onCollapse,
  onLogout,
  className,
}) => {
  return (
    <TooltipProvider>
      <aside 
        className={cn(
          // Base styles
          'h-screen flex flex-col',
          'bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950',
          'border-r border-white/5',
          'transition-colors duration-300 ease-in-out',
          'shadow-2xl shadow-black/20',
          // Width
          isCollapsed ? 'w-[72px]' : 'w-[280px]',
          className
        )}
      >
        {/* Logo Section */}
        <div className={cn(
          'flex items-center h-16 border-b border-white/5',
          isCollapsed ? 'justify-center px-3' : 'px-4 gap-3'
        )}>
          {/* Logo Icon */}
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-emerald-500/30">
            <span className="text-white font-bold text-lg">S</span>
          </div>
          
          {!isCollapsed && (
            <div className="flex-1">
              <div className="text-white font-semibold text-lg tracking-tight">SpeiseGuru</div>
              <div className="text-emerald-400/70 text-xs font-medium">Affiliate Portal</div>
            </div>
          )}
          
          {/* Collapse Button */}
          {onCollapse && !isCollapsed && (
            <button
              onClick={onCollapse}
              aria-label="Seitenleiste einklappen"
              className={cn(
                'p-1.5 rounded-lg',
                'text-white/40 hover:text-white hover:bg-white/10',
                'transition-colors duration-200'
              )}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Expand Button (when collapsed) */}
        {isCollapsed && onCollapse && (
          <button
            onClick={onCollapse}
            aria-label="Seitenleiste ausklappen"
            className={cn(
              'mx-3 mt-3 p-2 rounded-lg',
              'text-white/40 hover:text-white hover:bg-white/10',
              'transition-colors duration-200'
            )}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        )}

        {/* Navigation */}
        <nav aria-label="Affiliate Portal Navigation" className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {NAV_ITEMS.map((item) => (
            <NavItemButton
              key={item.id}
              item={item}
              isActive={activeRoute === item.href}
              isCollapsed={isCollapsed}
              onClick={() => onNavigate(item.href)}
            />
          ))}
        </nav>

        {/* User Section */}
        <UserSection 
          user={user} 
          isCollapsed={isCollapsed} 
          onLogout={onLogout}
        />
      </aside>
    </TooltipProvider>
  );
};

export default AffiliateSidebar;
